import { type Theme } from '@biome-design-system/styles';
import isFunction from 'lodash/isFunction';

export type ThemeContext = {
  theme: Theme;
  ownerState: Record<string, unknown>;
};

/**
 * Get the style overrides for a component.
 * @param overrides - The overrides to apply.
 * @param context - The context for the overrides, including the theme and owner state.
 * @returns The resolved style overrides.
 */
export const getOverrides = <T>(
  overrides: T,
  context: { theme: Theme; ownerState: Record<string, unknown> }
) => {
  const { theme, ownerState } = context;
  return isFunction(overrides) ? overrides({ theme, ownerState }) : {};
};

/**
 * Merges existing theme variants with new variants, allowing overrides based on props
 * @param existingVariants - The existing variants from the base theme
 * @param newVariants - The new variants to add or use for overriding
 * @returns Merged array of variants with new variants taking precedence, but individual variant properties are merged
 */
export const mergeVariants = (
  existingVariants: Array<any> = [],
  newVariants: Array<any> = []
) => {
  const variantMap = new Map();

  existingVariants.forEach((variant) => {
    const key = JSON.stringify(variant.props);
    variantMap.set(key, variant);
  });

  newVariants.forEach((newVariant) => {
    const key = JSON.stringify(newVariant.props);
    const existingVariant = variantMap.get(key);

    if (existingVariant) {
      const mergedVariant = {
        ...existingVariant,
        ...newVariant,
        style: (context: ThemeContext) => {
          const existingStyles = isFunction(existingVariant.style)
            ? existingVariant.style(context)
            : existingVariant.style || {};
          const newStyles = isFunction(newVariant.style)
            ? newVariant.style(context)
            : newVariant.style || {};

          return {
            ...existingStyles,
            ...newStyles
          };
        }
      };
      variantMap.set(key, mergedVariant);
    } else {
      variantMap.set(key, newVariant);
    }
  });

  return Array.from(variantMap.values());
};
